import React, { Component } from 'react';
import styles from './ImportPage.module.scss';
import LayoutComponent from '../../../../components/LayoutComponent/LayoutComponent';
import CustomContext from '../../../../context/CustomContext';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { FormInstance, Steps, Select } from 'antd';
import { Project, ProjectMember, EntityWithStatus } from '../../../../model/entities';
import { RouteComponentProps } from 'react-router';
import notificationService from '../../../../services/NotificationService';
import ResultComponent from './ResultComponent/ResultComponent';
import projectApi from '../../../../api/ProjectApi';
import UploadComponent from './UploadComponent/UploadComponent';
import SummaryComponent from './SummaryComponent/SummaryComponent';
import { ProviderType } from '../../../../model/types';
import stringService from '../../../../services/StringService';

class ImportPage extends Component<Props, State> {
    static contextType = CustomContext;
    context!: React.ContextType<typeof CustomContext>;
    formRef = React.createRef<FormInstance>();

    constructor(props: Props) {
        super(props);
        this.state = {
            step: 0,
            projects: [],
            selectedProjectIds: [],
            projectMembers: [],
        };
    }

    componentDidMount() {
        this.init();
    }

    /** METHODS **/

    init = async () => {
        try {
            const { provider, hubId } = this.props.match.params;
            const params = new URLSearchParams(this.props.location.search);
            const projects = await projectApi.list(provider, hubId);
            const selectedProjectIds = params.getAll('projectId');
            this.setState({ projects, selectedProjectIds });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        }
    };

    changeProjects = async (selectedProjectIds: string[]) => {
        this.setState({ selectedProjectIds });
    };

    showProjectMembers = (projectMembers: ProjectMember[]) => {
        const step = 1;
        this.setState({ step, projectMembers });
    };

    showResults = (result: EntityWithStatus<ProjectMember>[]) => {
        const step = 2;
        this.setState({ step, result });
    };

    back = () => {
        const step = this.state.step - 1;
        this.setState({ step });
    };

    /*** COMPONENTS ***/

    renderContent = (): React.ReactElement | undefined => {
        const { hubId } = this.props.match.params;
        const { step, projects, selectedProjectIds, projectMembers, result } = this.state;
        const selectedProjects = projects.filter((p) => selectedProjectIds.includes(p.id));

        return (
            <>
                {this.renderHeader()}

                <Steps current={step} className={styles.steps}>
                    <Steps.Step title={<FormattedMessage id="projectMembers.import.steps.1" />} />
                    <Steps.Step title={<FormattedMessage id="projectMembers.import.steps.2" />} />
                    <Steps.Step title={<FormattedMessage id="projectMembers.import.steps.3" />} />
                </Steps>

                <div hidden={step !== 0}>
                    <UploadComponent
                        hubId={hubId}
                        projects={selectedProjects}
                        onLoadProjectMembers={this.showProjectMembers}
                    />
                </div>
                {step === 1 && (
                    <SummaryComponent
                        hubId={hubId}
                        projects={selectedProjects}
                        projectMembers={projectMembers}
                        onSaveMembers={this.showResults}
                        onBack={this.back}
                    />
                )}
                {step === 2 && <ResultComponent result={result!} hubId={hubId} projects={selectedProjects} />}
            </>
        );
    };

    renderHeader = (): React.ReactElement | undefined => {
        const { projects, selectedProjectIds, step } = this.state;
        const selectedProjects = projects.filter((p) => selectedProjectIds.includes(p.id));
        const projectOptions = projects
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((project) => (
                <Select.Option key={project.id} value={project.id}>
                    {project.name}
                </Select.Option>
            ));

        return (
            <>
                <h1 className={styles.title}>
                    <FormattedMessage id="projectMembers.import.title" />
                </h1>
                {step === 0 && (
                    <>
                        <h4>
                            <FormattedMessage id="projectMembers.import.projects" />
                        </h4>
                        <Select
                            size="large"
                            allowClear
                            className={styles.projects}
                            value={selectedProjectIds}
                            onChange={this.changeProjects}
                            showSearch
                            filterOption={stringService.filterOptions}
                            mode={'multiple'}
                        >
                            {projectOptions}
                        </Select>
                    </>
                )}
                {step > 0 && <h4 className={styles.projects}>{selectedProjects.map((p) => p.name).join(', ')}</h4>}
            </>
        );
    };

    render() {
        return <LayoutComponent pageId="projectMembers">{this.renderContent()}</LayoutComponent>;
    }
}
export default injectIntl(ImportPage);

type ParamsType = { provider: ProviderType; hubId: string };
interface Props extends RouteComponentProps<ParamsType>, WrappedComponentProps {}

interface State {
    step: number;
    projects: Project[];
    selectedProjectIds: string[];
    projectMembers: ProjectMember[];
    result?: EntityWithStatus<ProjectMember>[];
    saving?: boolean;
    loading?: boolean;
}
